import { useState, type ReactElement } from 'react';
import { TableActionsCell } from '@/features/shared/components/tableSystem/TableCells';
import { buildSuiteDetailPath } from '@/features/shared/components/tableSystem/tableRoutes';
import { TableV2DurationCell } from '@/features/shared/components/tableSystem/TableV2DurationCell';
import { TableV2NestedFrame } from '@/features/shared/components/tableSystem/TableV2NestedFrame';
import {
  buildTableV2NestedBodyRowSx,
  resolveTableV2NestedDividerColor,
  tableV2NestedDepthSx,
} from '@/features/shared/components/tableSystem/TableV2NestedTable';
import {
  TableV2ActionsHeaderIcon,
  TableV2DateTimeCell,
  TableV2NameCell,
  TableV2NestedCollapse,
  TableV2SortableHeader,
  TableV2TagGroup,
  tableV2ChevronCellSx,
  tableV2NestedBodyRowHeightPx,
  tableV2NestedHeaderHeight,
} from '@/features/shared/components/tableSystem/TableV2Primitives';
import { formatTableV2RunNumber } from '@/features/shared/components/tableSystem/TableV2RunFormatting';
import { resolveEntityTags } from '@/features/shared/tags';
import {
  resolveProjectDisplayStatus,
  resolveProjectDisplayStatusLabel,
} from '@/features/shared/utils/projectStatusPresentation';
import { getEntityActionLabel } from '@/shared/copy/actions';
import { iconSizes, s, typographyTokens } from '@/theme/tokens';
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Text,
} from '@/ui/primitives';
import { KeyboardArrowDownIcon } from '@/ui/primitives/icons';
import {
  countTestSummaries,
  defaultNestedTestRunSort,
  getSummaryName,
  sortTestRunSummaries,
  type SortableNestedTestRunField,
} from './ExecutionRunsTableV2NestedSorting';
import {
  collapseExecutionRunCounts,
  nestedCarrierRowSx,
  nestedDetailCellSx,
  nestedDetailContentSx,
} from './ExecutionRunsTableV2RowHelpers';
import { PassRateCell, StatusChip } from './ExecutionRunsTableV2RowShared';
import { TestRunDetailRow } from './ExecutionRunsTableV2TestRunDetailRow';
import type { TagColor } from '@/shared/data/tags';
import type {
  ExecutionRunDto,
  SuiteRunDto,
  SuiteRunSummaryDto,
  TestRunDto,
  TestRunSummaryDto,
} from '@flowqa/shared';

const resolveSummaryTimestamp = (
  timestamp: Date | null | undefined,
  parent: ExecutionRunDto,
): Date =>
  timestamp ?? parent.startedAt ?? parent.finishedAt ?? parent.resultsFileCreatedAt ?? new Date(0);

const buildSuiteRunFromSummary = (
  summary: SuiteRunSummaryDto,
  parent: ExecutionRunDto,
): SuiteRunDto | null => {
  if (!summary.executionId) return null;
  const timestamp = resolveSummaryTimestamp(summary.timestamp, parent);
  return {
    id: summary.executionId,
    runId: parent.id,
    resultsFileId: parent.resultsFileId ?? null,
    suiteId: summary.suiteId,
    runNumber: summary.runNumber ?? null,
    suiteName: summary.suiteName ?? null,
    displayName: summary.displayName ?? null,
    suiteSlug: summary.suiteSlug ?? null,
    tags: summary.tags ?? [],
    projectId: parent.projectId,
    projectName: parent.projectName ?? null,
    projectSlug: parent.projectSlug ?? null,
    organisationId: parent.organisationId,
    createdById: parent.createdById,
    status: summary.status ?? 'untested',
    timestamp,
    durationMs: summary.durationMs ?? null,
    createdAt: parent.resultsFileCreatedAt ?? timestamp,
    deletedAt: null,
  };
};

export const SuiteRunDetailRow = ({
  deleteRowPendingId,
  editRowPendingId,
  groupErroredWithFailed,
  isLastSuite,
  parent,
  tagColorByName,
  testRunDeleteRowPendingId,
  testRunEditRowPendingId,
  suite,
  tests,
  onDelete,
  onDeleteTestRun,
  onEdit,
  onEditTestRun,
}: {
  deleteRowPendingId: string | null;
  editRowPendingId: string | null;
  groupErroredWithFailed: boolean;
  isLastSuite: boolean;
  parent: ExecutionRunDto;
  tagColorByName: Partial<Record<string, TagColor>>;
  testRunDeleteRowPendingId: string | null;
  testRunEditRowPendingId: string | null;
  suite: SuiteRunSummaryDto;
  tests: TestRunSummaryDto[];
  onDelete: (row: SuiteRunDto) => void;
  onDeleteTestRun: (row: TestRunDto) => void;
  onEdit: (row: SuiteRunDto) => void;
  onEditTestRun: (row: TestRunDto) => void;
}): ReactElement => {
  const [expanded, setExpanded] = useState(false);
  const [testRunSort, setTestRunSort] = useState<{
    field: SortableNestedTestRunField;
    direction: 'asc' | 'desc';
  }>(defaultNestedTestRunSort);
  const suiteName = getSummaryName(suite);
  const suiteRunNumber = suite.runNumber ?? null;
  const suiteRunNumberLabel = formatTableV2RunNumber(suiteRunNumber);
  const suiteHref = buildSuiteDetailPath(suite.suiteSlug ?? suite.suiteId);
  const suiteRunHref =
    suite.suiteSlug && suiteRunNumber !== null
      ? `/suites/${suite.suiteSlug}/runs/${suiteRunNumber}`
      : null;
  const counts = countTestSummaries(tests);
  const ranAt = suite.timestamp ?? null;
  const tags = resolveEntityTags(suite);
  const suiteRun = buildSuiteRunFromSummary(suite, parent);
  const sortedTests = sortTestRunSummaries(tests, testRunSort);
  const displayStatus = resolveProjectDisplayStatus(
    suite.status ?? 'untested',
    groupErroredWithFailed,
  );
  const displayStatusLabel = resolveProjectDisplayStatusLabel(
    suite.status ?? 'untested',
    groupErroredWithFailed,
  );
  const displayCounts = collapseExecutionRunCounts(counts, groupErroredWithFailed);

  const handleTestRunSortChange = (field: SortableNestedTestRunField): void => {
    setTestRunSort((current) => ({
      field,
      direction: current.field === field && current.direction === 'desc' ? 'asc' : 'desc',
    }));
  };

  return (
    <>
      <TableRow
        sx={[
          buildTableV2NestedBodyRowSx({
            depth: 1,
            hasRenderedDivider: !expanded && !isLastSuite,
          }),
          {
            '& > td': {
              boxSizing: 'border-box',
              maxHeight: `${String(tableV2NestedBodyRowHeightPx)}px`,
              overflow: 'hidden',
            },
          },
        ]}
      >
        <TableCell align="center" sx={tableV2ChevronCellSx}>
          <IconButton
            aria-label={expanded ? 'Collapse suite tests' : 'Expand suite tests'}
            aria-expanded={expanded}
            size="small"
            onClick={() => {
              setExpanded((current) => !current);
            }}
            sx={{
              alignItems: 'center',
              color: 'text.primary',
              display: 'inline-flex',
              height: s('2xl'),
              justifyContent: 'center',
              left: '50%',
              lineHeight: 0,
              p: s('none'),
              position: 'absolute',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              verticalAlign: 'middle',
              width: s('2xl'),
              '& > span': {
                height: iconSizes.lg,
                width: iconSizes.lg,
              },
            }}
          >
            <KeyboardArrowDownIcon
              sx={{
                transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                transition: (theme) => theme.transitions.create('transform', { duration: 160 }),
              }}
            />
          </IconButton>
        </TableCell>
        <TableCell>
          <TableV2NameCell
            href={suiteHref}
            primary={suiteName}
            secondary={suiteRunNumberLabel}
            secondaryHref={suiteRunHref}
          />
        </TableCell>
        <TableCell>
          <StatusChip status={displayStatus} label={displayStatusLabel} />
        </TableCell>
        <TableCell>
          <PassRateCell counts={displayCounts} />
        </TableCell>
        <TableCell>
          <TableV2DurationCell value={suite.durationMs ?? null} />
        </TableCell>
        <TableCell>
          <TableV2DateTimeCell value={ranAt} />
        </TableCell>
        <TableCell>
          <TableV2TagGroup tagColorByName={tagColorByName} tags={tags} />
        </TableCell>
        <TableCell align="center">
          <TableActionsCell
            rowName={suiteName}
            verticalAlign="center"
            disabledTooltip={suiteRun ? null : 'Actions unavailable for this suite run.'}
            editActionLabel={getEntityActionLabel('edit', 'Suite Run')}
            deleteActionLabel={getEntityActionLabel('delete', 'Suite Run')}
            onEdit={
              suiteRun
                ? () => {
                    onEdit(suiteRun);
                  }
                : undefined
            }
            onDelete={
              suiteRun
                ? () => {
                    onDelete(suiteRun);
                  }
                : undefined
            }
            disableEdit={
              suiteRun
                ? editRowPendingId === suiteRun.id || deleteRowPendingId === suiteRun.id
                : true
            }
            disableDelete={suiteRun ? deleteRowPendingId === suiteRun.id : true}
          />
        </TableCell>
      </TableRow>
      <TableRow
        sx={[
          nestedCarrierRowSx,
          expanded && !isLastSuite
            ? {
                '& > td': {
                  borderBottom: (theme) => `1px solid ${theme.app.colors.border}`,
                },
              }
            : {},
        ]}
      >
        <TableCell
          colSpan={8}
          sx={
            expanded
              ? [
                  nestedDetailCellSx,
                  {
                    borderBottom: isLastSuite ? 0 : undefined,
                  },
                ]
              : { borderBottom: 0, p: s('none') }
          }
        >
          <TableV2NestedCollapse expanded={expanded}>
            <Box sx={nestedDetailContentSx}>
              {sortedTests.length > 0 ? (
                <TableV2NestedFrame depth={2}>
                  <Table
                    aria-label="Test runs"
                    sx={[
                      tableV2NestedDepthSx[2].table,
                      {
                        tableLayout: 'fixed',
                        '& th': {
                          borderBottom: (theme) =>
                            `1px solid ${resolveTableV2NestedDividerColor(theme)}`,
                          color: 'text.primary',
                          fontSize: typographyTokens.body.fontSize,
                          fontWeight: typographyTokens.body.fontWeight,
                          height: tableV2NestedHeaderHeight,
                          py: s('none'),
                          verticalAlign: 'middle',
                        },
                        '& tbody tr:last-of-type > td': {
                          borderBottom: 0,
                        },
                        '& td': {
                          fontSize: typographyTokens.body.fontSize,
                        },
                        '& .MuiTypography-root': {
                          fontSize: typographyTokens.body.fontSize,
                        },
                      },
                    ]}
                  >
                    <colgroup>
                      <col width="24%" />
                      <col width="11%" />
                      <col width="13%" />
                      <col width="24%" />
                      <col width="22%" />
                      <col width="4%" />
                    </colgroup>
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <TableV2SortableHeader
                            activeSort={testRunSort}
                            field="runNumber"
                            label="Test Run"
                            onSortChange={handleTestRunSortChange}
                          />
                        </TableCell>
                        <TableCell>
                          <TableV2SortableHeader
                            activeSort={testRunSort}
                            field="status"
                            label="Status"
                            onSortChange={handleTestRunSortChange}
                          />
                        </TableCell>
                        <TableCell>
                          <TableV2SortableHeader
                            activeSort={testRunSort}
                            field="durationMs"
                            label="Duration"
                            onSortChange={handleTestRunSortChange}
                          />
                        </TableCell>
                        <TableCell>
                          <TableV2SortableHeader
                            activeSort={testRunSort}
                            field="timestamp"
                            label="Ran At"
                            onSortChange={handleTestRunSortChange}
                          />
                        </TableCell>
                        <TableCell>Tags</TableCell>
                        <TableCell
                          align="center"
                          sx={{ height: tableV2NestedHeaderHeight, p: s('none') }}
                        >
                          <TableV2ActionsHeaderIcon />
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedTests.map((test) => (
                        <TestRunDetailRow
                          key={`${test.testId}:${test.executionId ?? ''}`}
                          deleteRowPendingId={testRunDeleteRowPendingId}
                          editRowPendingId={testRunEditRowPendingId}
                          parent={parent}
                          suiteRunId={suite.executionId ?? null}
                          suiteSlug={suite.suiteSlug ?? null}
                          groupErroredWithFailed={groupErroredWithFailed}
                          tagColorByName={tagColorByName}
                          test={test}
                          onDelete={onDeleteTestRun}
                          onEdit={onEditTestRun}
                        />
                      ))}
                    </TableBody>
                  </Table>
                </TableV2NestedFrame>
              ) : (
                <Text role="body" color="textSecondary">
                  No tests available for this suite.
                </Text>
              )}
            </Box>
          </TableV2NestedCollapse>
        </TableCell>
      </TableRow>
    </>
  );
};
